import { toast } from 'react-toastify';
import { callAPI } from './apiUtils';
import { apiUrls } from './apiUrls';
import { parseScan, orderLookupCode, memberLookupCode } from './scanRouter';
import { MEMBER_EVENT, memberLabel, rememberMember } from './memberScan';

const DEBOUNCE_MS = 1500;
let lastCode = null;
let lastAt = 0;

/** Forget the duplicate-scan memory (tests). */
export const resetScanDebounce = () => {
  lastCode = null;
  lastAt = 0;
};

const short = (v) => (v.length > 24 ? `${v.slice(0, 24)}...` : v);

/**
 * Acts on one scanned text. `ctx` = { navigate, pathname }.
 * Returns the parsed scan when it was acted on, or null when it was ignored (unknown, duplicate, logged out).
 */
export async function handleScan(text, ctx = {}) {
  const parsed = parseScan(text);
  if (parsed.type === 'unknown') return null;
  if (!localStorage.getItem('accessToken')) return null;

  const key = `${parsed.type}:${parsed.value}`;
  const now = Date.now();
  if (key === lastCode && now - lastAt < DEBOUNCE_MS) return null;
  lastCode = key;
  lastAt = now;

  if (parsed.type === 'voucher') {
    // The apply-voucher modal, when open, listens for this and fills the code and applies it.
    const detail = { code: parsed.value, handled: false };
    window.dispatchEvent(new CustomEvent('scan:voucher', { detail }));
    if (!detail.handled) toast.info('Open Apply voucher, then scan again');
    return parsed;
  }

  if (parsed.type === 'member') {
    try {
      const res = await callAPI(`${apiUrls.MEMBERSHIP_LOOKUP}?code=${encodeURIComponent(memberLookupCode(parsed))}`, {}, 'GET', null, {}, {}, false);
      const member = res?.data?.data;
      if (res?.data?.status === 1 && member?.customer_id) {
        // customer selection and checkout take the card at once; anywhere else it waits for the next customer selection
        const detail = { member, handled: false };
        window.dispatchEvent(new CustomEvent(MEMBER_EVENT, { detail }));
        if (!detail.handled) {
          rememberMember(member);
          toast.info(`Member: ${memberLabel(member)}. They will be added when you choose the customer.`);
        }
      } else {
        toast.error('Membership card not found for this restaurant');
      }
    } catch (err) {
      if (err?.response?.status === 404) toast.error('Membership card not found for this restaurant');
      else if (err?.response?.status === 429) toast.error(err.response?.data?.message || 'Too many lookups. Wait a minute and try again.');
      else if (!err?.response) toast.error('Could not reach the server to look up the card');
      else toast.error(err.response?.data?.message || 'Could not look up that card');
    }
    return parsed;
  }

  // order
  if (ctx.pathname === '/payment-view') {
    toast.info('Finish or leave this payment before opening another order');
    return parsed;
  }
  toast.info(`Scanned order ${short(parsed.value)}`);
  try {
    const res = await callAPI(`${apiUrls.ORDER_LOOKUP}?code=${encodeURIComponent(orderLookupCode(parsed))}`, {}, 'GET', null, {}, {}, false);
    const id = res?.data?.data?.id;
    if (res?.data?.status === 1 && id) {
      ctx.navigate?.(`/orders?scan_order_id=${encodeURIComponent(id)}&scan_ts=${Date.now()}`);
    } else {
      toast.error('Order not found for this restaurant');
    }
  } catch (err) {
    if (err?.response?.status === 404) toast.error('Order not found for this restaurant');
    else if (!err?.response) toast.error('Could not reach the server to look up the order');
    else toast.error(err.response?.data?.message || 'Could not look up that order');
  }
  return parsed;
}
